import React, { useState, useMemo, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Skeleton,
  Space,
  Form,
  Collapse,
  message,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import dayjs from "dayjs";
import { asyncSaveLeaseDraft } from "../../../../../store/easyApp/easyapp-async";
import { getPropertyInfoService, getUnitDetailsService } from "../../../../../services/easyapp.service";
import { ApplicantCard } from "./components/Applicant-Card";
import { PetsAndVehicles } from "./components/Pet-Vehicle-Card";
import { LeaseTermsForm } from "./components/Lease-Term-Form";
import { ChargesForm } from "./components/Charges-Form";
import { ConcessionsForm } from "./components/OtherInfo";
import { AddendaForm } from "./components/Addenda-Form";

const CreateStep: React.FC<CreateStepProps> = ({
  loading,
  selectedAppGroup,
  plaidIncome,
  unitRent,
  selectedPropertyFeeRecord,
  transactionCategories,
}) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [editMode, setEditMode] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [activePanel, setActivePanel] = useState<string | string[] | undefined>(
    undefined
  );

  const { user } = useAppSelector((state) => state.user as any);

  const handleSaveDraft = useCallback(async () => {
    try {
      setSavingDraft(true);
      await form.validateFields();
      const formValues = form.getFieldsValue();
      const [propertyRes, unitRes]: any = await Promise.all([
        getPropertyInfoService(selectedAppGroup.ms_property_id),
        getUnitDetailsService(
          selectedAppGroup.ms_property_id,
          selectedAppGroup.desired_apartment
        ),
      ]);

      // Get current rent value from form
      const currentRent = formValues.rent ?? unitRent ?? 0;

      const previousAddendaData = selectedAppGroup?.leaseDraft?.addendaData || {};
        const mergedAddendaData = {
          ...previousAddendaData,
          ...(formValues.addendaData || {}),
        };

      const draftData = {
        ...formValues,
        savedAt: new Date().toISOString(),
        savedUnit: selectedAppGroup.desired_unit_number, // Track the unit number at time of save
        moveInDate: formValues.moveInDate
          ? formValues.moveInDate.startOf("day").toISOString()
          : null,
        moveOutDate: formValues.moveOutDate
          ? formValues.moveOutDate.startOf("day").toISOString()
          : null,
        // Update rent in fees array with current rent value
        fees: formValues.fees.map((fee: any) => {
          const category = transactionCategories.find(
            (c: any) => c.ID === fee.feeType
          );
          const isRentFee = category?.Name.toLowerCase() === "rent";

          return {
            ...fee,
            startDate: fee.startDate ? fee.startDate.startOf("day").toISOString() : null,
            frequency: fee.frequency || (isRentFee ? "Monthly" : "One-Time"),
            feeName: category?.Name || "Fee",
            // Use current rent for rent fee, keep other amounts
            amount: isRentFee ? currentRent : fee.amount,
          };
        }),
        otherFees: formValues.otherFees.map((fee: any) => ({
          ...fee,
          startDate: fee.startDate ? fee.startDate.startOf("day").toISOString() : null,
          frequency: fee.frequency || "One-Time",
          feeName:
            transactionCategories.find((c: any) => c.ID === fee.feeType)
              ?.Name || "Fee",
        })),
        proration: formValues.proration || null,
        leaseholders: formValues.leaseholders.map((id: string) => {
          const applicant = selectedAppGroup.applicants.find(
            (a: any) => a.application_id === String(id)
          );
          return {
            id: String(id),
            first_name: applicant.first_name,
            last_name: applicant.last_name,
            email: applicant.email,
          };
        }),
        occupants: formValues.occupants.map((id: string) => {
          // Check if this is a child (ID starts with "child-")
          if (id.startsWith("child-")) {
            // Parse child ID: "child-{applicantId}-{index}"
            const parts = id.split("-");
            const applicantId = parts.slice(1, -1).join("-"); // Handle UUIDs with dashes
            const childIndex = parseInt(parts[parts.length - 1], 10);
            
            const applicant = selectedAppGroup.applicants.find(
              (a: any) => a.application_id === applicantId
            );
            const children = applicant?.children || applicant?.minors || [];
            const child = children[childIndex];
            
            if (child) {
              const firstName = child.first_name || child.firstName || "";
              const lastName = child.last_name || child.lastName || "";
              const dob = child.dob || child.dateOfBirth;
              return {
                id: String(id),
                first_name: firstName,
                last_name: lastName,
                email: "", // Children don't have email
                age: dob ? dayjs().diff(dayjs(dob), "year") : 0,
              };
            }
          }
          
          // Handle adult applicants
          const applicant = selectedAppGroup.applicants.find(
            (a: any) => a.application_id === String(id)
          );
          return {
            id: String(id),
            first_name: applicant?.first_name || "",
            last_name: applicant?.last_name || "",
            email: applicant?.email || "",
            age: applicant?.dob ? dayjs().diff(dayjs(applicant.dob), "year") : 0,
          };
        }),
        addenda: formValues.addenda || [],
        addendaData: mergedAddendaData || {},
        concessions: formValues.concessions || [],
        conditions: formValues.conditions || [],
        propertyContact: {
          address: {
            Address: unitRes?.data?.streetAddress || null,
            City: unitRes?.data?.city || null,
            State: unitRes?.data?.state || null,
            Zip: unitRes?.data?.zip || null,
          },
          phone:
            propertyRes?.data?.ResMan?.Response?.PhysicalProperty?.Property?.PropertyID
              ?.Phone?.PhoneNumber || null,
        },
        pets: formValues.pets || [],
        trashFee: formValues.trashFee ?? 12,
        taxFee: formValues.taxFee ?? 0,
        inspectionFee: formValues.inspectionFee ?? 100,
        parkingPermitReplacementFee: formValues.parkingPermitReplacementFee ?? 50,
        vehicles: Array.from(
          new Map(
            (
              selectedAppGroup?.applicants?.flatMap(
                (applicant: any) => applicant.vehicles || []
              ) || []
            ).map((vehicle: any) => [
              vehicle.lic?.trim().toLowerCase(),
              vehicle,
            ])
          ).values()
        ),
        owner: {
          name: user?.name || "",
          email: user?.username || "",
        },
        // Store rent separately for easier access
        rent: currentRent,
      };

      await dispatch(
        asyncSaveLeaseDraft({
          leaseDraft: draftData,
          appGroupID: selectedAppGroup.application_group,
        })
      );
      message.success("Draft saved successfully");
    } catch (error) {
      message.error("Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  }, [form, dispatch, selectedAppGroup, transactionCategories, unitRent, user]);

  const handleFormChange = useCallback(
    (changedValues: any, values: any) => {
      if (changedValues.leaseholders) {
        form.setFieldsValue({
          occupants: Array.from(
            new Set([
              ...(values.occupants || []),
              ...changedValues.leaseholders,
            ])
          ),
        });
      }
    },
    [form]
  );

  const collapseItems = useMemo(() => {
    if (!selectedAppGroup?.applicants?.length) return [];
    return selectedAppGroup.applicants.map((applicant: any, index: number) => {
      const key = applicant.application_id || `applicant-${index}`;
      return {
        key,
        label: (
          <Space align="center" wrap>
            <Typography.Text strong style={{ fontSize: 14 }}>
              {applicant.first_name} {applicant.last_name}
            </Typography.Text>
            {applicant.gurantor && <Tag color="processing">Guarantor</Tag>}
            {applicant.payment_received && (
              <Tag color="green">Payment Received</Tag>
            )}
          </Space>
        ),
        children: (
          <ApplicantCard
            applicant={applicant}
            index={index}
            incomeData={plaidIncome?.data}
            unitRent={unitRent}
          />
        ),
        showArrow: true,
        style: { marginBottom: 8 },
      };
    });
  }, [selectedAppGroup?.applicants, plaidIncome?.data, unitRent]);

  if (
    loading ||
    !Array.isArray(selectedAppGroup?.applicants)
  ) {
    return (
      <div style={{ padding: "24px 0" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Col>
          <Col xs={24} md={12}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Col>
        </Row>
      </div>
    );
  }
  
  return (
    <div style={{ padding: "24px 0" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
        }}
      >
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => setEditMode(!editMode)}
        >
          <span
            className="transition-opacity duration-600 ease-in-out"
            key={editMode ? "view" : "edit"}
          >
            {editMode ? "View Mode" : "Edit Details"}
          </span>
        </Button>
      </div>
      <Form
        form={form}
        layout="vertical"
        disabled={!editMode}
        onValuesChange={handleFormChange}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <LeaseTermsForm
              form={form}
              editMode={editMode}
              selectedAppGroup={selectedAppGroup}
              plaidIncome={plaidIncome}
              transactionCategories={transactionCategories}
              unitRent={unitRent}
              selectedPropertyFeeRecord={selectedPropertyFeeRecord}
            />
          </Col>
          <Col xs={24} md={12}>
            <Card
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                marginBottom: 16,
              }}
              title={<Typography.Title level={4}>Applicants</Typography.Title>}
            >
              {selectedAppGroup.applicants.length === 0 ? (
                <Typography.Text type="secondary">
                  No applicants available
                </Typography.Text>
              ) : (
                <Collapse
                  accordion
                  activeKey={activePanel}
                  onChange={(key) => setActivePanel(key)}
                  items={collapseItems}
                />
              )}
            </Card>
            <PetsAndVehicles applicants={selectedAppGroup?.applicants} />
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <ChargesForm
              form={form}
              editMode={editMode}
              transactionCategories={transactionCategories}
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <ConcessionsForm form={form} editMode={editMode} />
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <AddendaForm
              form={form}
              editMode={editMode}
              selectedPropertyFeeRecord={selectedPropertyFeeRecord}
            />
          </Col>
        </Row>
      </Form>
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Button
          type="default"
          onClick={handleSaveDraft}
          disabled={savingDraft || !editMode}
          loading={savingDraft}
        >
          Save Draft
        </Button>
      </div>
    </div>
  );
};

export default CreateStep;
